import '@shared/utils/dayjs.config';

import {
  isPlatformBrowser,
  LocationStrategy,
  PathLocationStrategy,
} from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
} from '@angular/core';
import {provideClientHydration} from '@angular/platform-browser';
import {provideRouter, withInMemoryScrolling} from '@angular/router';
import {BCC_LANGUAGE_PACKS} from '@assets/i18n/translations.enums';
import {
  BNT_MODALS,
  BNT_MODALS_CONFIG,
  BntSentryErrorHandler,
  provideBntConfig,
  provideBntSentry,
} from '@binotel-frontend/bnt-common-components';
import {environment} from '@envs/environment';
import {consumerIcons} from '@figma-icons/figma-icons';
import {WA_LOCAL_STORAGE, WA_WINDOW} from '@ng-web-apis/common';
import {MODALS} from '@shared/entities/ui/modals/modals';
import {authInterceptor} from '@shared/interceptors/auth.interceptor';
import {ssrTransferInterceptor} from '@shared/interceptors/ssr-transfer.interceptor';
import {unauthorizedInterceptor} from '@shared/interceptors/unathorized.interceptor';
import {AppInitializerService} from '@shared/services/app-initializer.service';
import {NGRX_PROVIDERS} from '@state/store';

import {routes} from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    ...provideBntConfig({
      environment,
      languagePacks: BCC_LANGUAGE_PACKS,
      icons: consumerIcons,
    }),
    provideBntSentry(environment),
    ...(environment.sentryDsn
      ? [{provide: ErrorHandler, useClass: BntSentryErrorHandler}]
      : []),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      })
    ),
    provideClientHydration(),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor,
        unauthorizedInterceptor,
        ssrTransferInterceptor,
      ])
    ),
    ...NGRX_PROVIDERS,
    provideAppInitializer(() => inject(AppInitializerService).initializeApp()),
    {provide: BNT_MODALS_CONFIG, useValue: {closeIcon: null}},
    {provide: BNT_MODALS, useValue: MODALS},
    {provide: LocationStrategy, useClass: PathLocationStrategy},
    {
      provide: WA_LOCAL_STORAGE,
      useFactory: (): Storage | null => {
        if (!isPlatformBrowser(inject(PLATFORM_ID))) {
          return null;
        }

        try {
          return inject(WA_WINDOW).localStorage;
        } catch {
          return null;
        }
      },
    },
  ],
};
